<script>
  import { tourFunctions } from "../tour-functions";
  import { fly } from "svelte/transition";
  import { focusTrap } from "./utils.js";
  import { Preloader } from "./preloader.js";
  import i18n from "./i18n/i18n.js";
  import { setTrackSceneSource } from "./analytics.js";

  import UA from "ua-parser-js";
  const ua = new UA();
  let isMobile = ua.getResult().device["type"] == "mobile";

  import Tooltip from "./Tooltip.svelte";
  import { onMount, tick } from "svelte";
  let showingThumbsTooltip = false;
  const showThumbsTooltip = () => {
    showingThumbsTooltip = true;
  };
  const hideThumbsTooltip = () => {
    showingThumbsTooltip = false;
  };

  let showingPrevTooltip = false;
  const showPrevTooltip = () => {
    showingPrevTooltip = true;
  };
  const hidePrevTooltip = () => {
    showingPrevTooltip = false;
  };

  let showingNextTooltip = false;
  const showNextTooltip = () => {
    showingNextTooltip = true;
  };
  const hideNextTooltip = () => {
    showingNextTooltip = false;
  };

  export let title = "";
  export let tour;
  export let scenes = tour.scenes?.filter((scene) => !scene.hidden && !scene.burried) || [];
  export let nextScene = () => {
    tourFunctions.nextScene();
    preloader.preloadCurrentScene();
  };
  export let prevScene = () => {
    tourFunctions.prevScene('prev');
    preloader.preloadCurrentScene();
  };
  export let hasCategories = false;
  export let categoryStyle = "sidebar"; // select, buttons, sidebar
  let thumbsButton; // ref to thumbs button
  const preloader = new Preloader(tour);
  if (isMobile) categoryStyle = "select";

  let currentSceneName;
  function getCurrentSceneName() {
    currentSceneName = tourFunctions.currentScene.getName();
    return currentSceneName;
  }

  let currentCategory = null;
  let categoryInput;
  export let categories = []; // categories found in scenes
  let visibleScenes = scenes; // scenes for the current category

  export let showPrevNext = true;
  export let thumbsButtonOpen;
  export let thumbsButtonClose;

  let thumbsgrid; //bind on .thumbsgrid for scrolling behavior
  var thumbs = []; //bind on .thumbs for scrolling behavior
  let visible = false; // visibility of thumbs grid

  // Fire event every time thumb grid visibility changes
  $: if (visible || visible == false) {
    dispatchEvent(
      new CustomEvent("krpano:gridtoggle", {
        detail: { grid: visible },
      })
    );
  }

  // Gets categories when scenes[] is set
  $: if (scenes && hasCategories && categories.length === 0) {
    categories = getCategories();
  }

  // Converts title case --> Title Case
  function titleCase(str) {
    let words = str.toLowerCase().split(" ");
    for (let i = 0; i < words.length; i++) {
      words[i] = words[i].charAt(0).toUpperCase() + words[i].substring(1);
    }
    return words.join(" ");
  }

  function getCategories(tour) {
    const result = [];

    for (const category of categories) {
      let title = titleCase(category.name.replace("_", " "));
      result.push({ title: title, name: category.name });
    }

    return result;
  }

  function changeCategory(category) {
    currentCategory = category;

    if (category) {
      const categoryObj = categories.find((c) => c.id === category.id);
      if (categoryObj?.sceneIds?.length > 0) {
        // New approach using sceneIds
        visibleScenes = categoryObj.sceneIds
          .map((sceneId) => {
            return scenes.find((scene) => scene.id === sceneId);
          })
          .filter(Boolean);
      } else {
        // Fallback to old approach
        visibleScenes = scenes.filter(
          (scene) => scene.category === category.name
        );
      }
    } else {
      visibleScenes =
        Object.keys(scenes).length > 0 ? Object.values(scenes) : scenes; // Show all scenes
    }

    return visibleScenes;
  }

  function openThumbs() {
    if (scenes.length === 0) {
      scenes = window.CC.krpano.get("scene").getArray();
      visibleScenes = scenes;
      // announce the opening of the thumbs grid
      document.getElementById("liveFeedback").textContent =
        $i18n.t("tour.openedScenes");
    }

    getCurrentSceneName();
    scrollToActiveThumb();

    visible = true;
  }

  function closeThumbs() {
    visible = false;
    // announce the closing of the thumbs grid
    document.getElementById("liveFeedback").textContent =
      $i18n.t("tour.closedScenes");
    tourFunctions.focus();
  }

  function toggleThumbs() {
    visible ? closeThumbs() : openThumbs();
  }

  function sleep(milliseconds) {
    return new Promise((resolve) => setTimeout(resolve, milliseconds));
  }

  function getActiveSceneIndex() {
    return visibleScenes.findIndex((scene) => scene.name === currentSceneName);
  }

  function scrollToActiveThumb() {
    let activeSceneIndex = getActiveSceneIndex();
    if (activeSceneIndex !== -1 && thumbs[activeSceneIndex]) {
      window.scrollthumb = thumbs[activeSceneIndex];
      thumbs[activeSceneIndex].scrollIntoView();
    }
  }

  function loadScene(scene) {
    tourFunctions.loadScene(scene, 'grid');
    // announce the scene change
    document.getElementById("liveFeedback").textContent =
      `${$i18n.t("tour.loadScene")} ${scene.title}`;
    closeThumbs();
    preloader.preloadCurrentScene();
  }

  // keyboard shortcuts space opens/closes thumbs
  document.addEventListener("keydown", function (event) {
    // ? / key opens/closes thumbs
    if (event.keyCode === 191) {
      toggleThumbs();
    }

    // Escape key
    if (event.keyCode === 27) {
      visible = false;
    }
  });

  let thumbsContainer;
  let stillAnimating = false;
  let focusTrapInstance = null; // to hold the focus trap instance

  onMount(() => {
    thumbsContainer.addEventListener("transitionstart", () => {
      stillAnimating = true;
    });
    thumbsContainer.addEventListener("transitionend", () => {
      stillAnimating = false;
    });
  });

  async function handleFocus() {
    await tick();
    const activeLocationThumb = thumbs[getActiveSceneIndex()];
    // only allow one focus trap instance at a time
    if (focusTrapInstance) {
      focusTrapInstance.destroy();
      focusTrapInstance = null;
    }
    focusTrapInstance = focusTrap(thumbsContainer);
    // there are cases where the active thumb is not focusable (e.g. category hides the scene thumb)
    if (activeLocationThumb) {
      activeLocationThumb.querySelector("button").focus();
    }
  }
  $: if (visible && scenes.length && thumbs.length && !stillAnimating) {
    handleFocus();
  }
</script>

<div
  class="thumbs-container"
  class:active={visible}
  bind:this={thumbsContainer}
  role="dialog"
  aria-label={$i18n.t("tour.openScenes")}
>
  <header>
    <!-- Title -->
    <h1 class="tour-title">{@html title}</h1>
    <!-- Categories -->
    {#if hasCategories && categories.length > 0}
      <div class="scene-categories">
        <!-- buttons -->
        {#if categoryStyle === "buttons"}
          <h2>{$i18n.t("tour.selectCategory")}:</h2>
          <ol>
            <li class:active={!currentCategory}>
              <button
                class="category"
                on:click={() => changeCategory()}
                tabindex="0"
              >
                {$i18n.t("tour.categoryAll")}
              </button>
            </li>
            {#each categories as c}
              <li class:active={currentCategory.name === c.name}>
                <button
                  class="category"
                  on:click={() => changeCategory(c)}
                  tabindex="0"
                >
                  {c.name}
                </button>
              </li>
            {/each}
          </ol>
          <!-- select -->
        {:else if categoryStyle === "select"}
          <select
            id="categorySelect"
            bind:value={currentCategory}
            on:change={() => changeCategory(currentCategory)}
            bind:this={categoryInput}
          >
            <option value={null}>{$i18n.t("tour.categoryAll")}</option>
            {#each categories as c}
              <option value={c}>{c.name}</option>
            {/each}
          </select>
        {/if}
      </div>
    {/if}
  </header>

  <main
    class:has-category-sidebar={hasCategories && categoryStyle === "sidebar"}
  >
    {#if hasCategories && categoryStyle === "sidebar"}
      <nav class="category-sidebar">
        <h2>{$i18n.t('tour.selectCategory')}:</h2>
        <ol>
          <li class:active={!currentCategory}>
            <button
              tabindex="0"
              on:click={() => changeCategory()}
              class="category"
            >
            {$i18n.t("tour.categoryAll")}
            </button>
          </li>
          {#each categories as c}
            <li class:active={currentCategory?.name === c?.name}>
              <button
                tabindex="0"
                on:click={() => changeCategory(c)}
                class="category"
              >
                {c.name}
              </button>
            </li>
          {/each}
        </ol>
      </nav>
    {/if}
    <!-- Grid -->
    <nav class="thumbsgrid" bind:this={thumbsgrid}>
      <ol class="thumbsgrid-view">
        <!-- Scenes -->
        {#each visibleScenes as s, i}
          <li class:active={currentSceneName === s.name} bind:this={thumbs[i]}>
            <button
              on:click={loadScene(s)}
              tabindex="0"
              class="thumb"
              aria-label={`${$i18n.t('tour.openScene')} ${s.title}`}
            >
              <figure>
                <img
                  src={s.thumbUrl || s.thumburl}
                  width="150"
                  height="150"
                  alt={s.title}
                />
                <figcaption>
                  <p>{@html tourFunctions.parseContent(s.title)}</p>
                </figcaption>
              </figure>
            </button>
          </li>
        {/each}
      </ol>
    </nav>
  </main>
</div>
<!-- /  .thumbs-container -->
<!-- Button -->
<div class="bottom-menu" transition:fly={{ y: 300, duration: 500 }}>
  {#if isMobile || showPrevNext}
    <button
      class="prev-button"
      on:click={() => {
        setTrackSceneSource('previous');
        prevScene();
        visible = false;
      }}
      on:mouseenter={showPrevTooltip}
      on:mouseleave={hidePrevTooltip}
      aria-label={$i18n.t('tour.prevScene')}
    >
      <Tooltip
        text={$i18n.t('tour.prevScene')}
        showingTooltip={showingPrevTooltip}
        position={"top"}
      />
      <!-- Previous Arrow -->
      <div class="icon">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 19.71 32.59"
          ><polyline
            points="18.68 1.11 2.21 16.18 18.68 31.49"
            style="fill:none;stroke:var(--thumbs-grid-bottom-menu-text-color);stroke-miterlimit:10;stroke-width:2px"
          /></svg
        >
      </div>
    </button>
  {/if}
  <button
    class="thumbs-button"
    class:active={visible}
    on:click={toggleThumbs}
    on:mouseenter={showThumbsTooltip}
    on:mouseleave={hideThumbsTooltip}
    bind:this={thumbsButton}
    tabindex="0"
  >
    <!-- <Tooltip
      text={visible ? "Close Locations" : "Open Locations"}
      showingTooltip={showingThumbsTooltip}
      position={"top"}
    /> -->
    {#if visible}
      <!-- Close Locations -->
      <div class="icon">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 44.19 44.19"
          ><circle
            cx="22.1"
            cy="22.1"
            r="21.1"
            style="fill:none;stroke:var(--thumbs-grid-bottom-menu-text-color);;stroke-miterlimit:10;stroke-width:2px"
          /><path
            d="M20.63,11.24h3V26.42l5.81-6.1V25.8L22.1,33"
            style="fill:var(--thumbs-grid-bottom-menu-text-color);"
          /><path
            d="M23.57,11.24h-3V26.42l-5.81-6.1V25.8L22.09,33"
            style="fill:var(--thumbs-grid-bottom-menu-text-color);"
          /></svg
        >
      </div>
      {#if !isMobile}<div>{thumbsButtonClose}</div>{/if}
    {:else}
      <!-- Open Locations -->
      <div class="icon">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 44.19 44.19"
          ><rect
            x="11.63"
            y="11.58"
            width="5"
            height="5"
            style="fill:var(--thumbs-grid-bottom-menu-text-color);"
          /><rect
            x="19.64"
            y="11.58"
            width="5"
            height="5"
            style="fill:var(--thumbs-grid-bottom-menu-text-color);"
          /><rect
            x="27.73"
            y="11.58"
            width="5"
            height="5"
            style="fill:var(--thumbs-grid-bottom-menu-text-color);"
          /><rect
            x="11.59"
            y="19.59"
            width="5"
            height="5"
            style="fill:var(--thumbs-grid-bottom-menu-text-color);"
          /><rect
            x="19.59"
            y="19.59"
            width="5"
            height="5"
            style="fill:var(--thumbs-grid-bottom-menu-text-color);"
          /><rect
            x="27.69"
            y="19.59"
            width="5"
            height="5"
            style="fill:var(--thumbs-grid-bottom-menu-text-color);"
          /><rect
            x="11.7"
            y="27.61"
            width="5"
            height="5"
            style="fill:var(--thumbs-grid-bottom-menu-text-color);"
          /><rect
            x="19.7"
            y="27.61"
            width="5"
            height="5"
            style="fill:var(--thumbs-grid-bottom-menu-text-color);"
          /><rect
            x="27.8"
            y="27.61"
            width="5"
            height="5"
            style="fill:var(--thumbs-grid-bottom-menu-text-color);"
          /><circle
            cx="22.1"
            cy="22.1"
            r="21.1"
            style="fill:none;stroke:var(--thumbs-grid-bottom-menu-text-color);;stroke-miterlimit:10;stroke-width:2px"
          /></svg
        >
      </div>
      {#if !isMobile}<div>{thumbsButtonOpen}</div>{/if}
    {/if}
  </button>
  {#if isMobile || showPrevNext}
    <button
      class="next-button"
      on:click={() => {
        setTrackSceneSource('next');
        nextScene();
        visible = false;
      }}
      on:mouseenter={showNextTooltip}
      on:mouseleave={hideNextTooltip}
      aria-label={$i18n.t('tour.nextScene')}
    >
      <Tooltip
        text={$i18n.t('tour.nextScene')}
        showingTooltip={showingNextTooltip}
        position={"top"}
      />
      <!-- Next Arrow -->
      <div class="icon">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 19.71 32.59"
          ><polyline
            points="18.68 1.11 2.21 16.18 18.68 31.49"
            style="fill:none;stroke:var(--thumbs-grid-bottom-menu-text-color);stroke-miterlimit:10;stroke-width:2px"
          /></svg
        >
      </div>
    </button>
  {/if}
</div>

<style>
  /* Component Colors */
  :root {
    --thumbs-grid-text-color: #fff;
    --thumbs-grid-text-bg-color-rgb: 0, 0, 0;
    --thumbs-grid-container-bg-color-rgb: 0, 0, 0;
    --thumbs-grid-bottom-menu-bg-color-rgb: var(--theme-color-rgb);
    --thumbs-grid-bottom-menu-text-color: var(--controls-text-color);
  }

  h1.tour-title {
    text-align: center;
    font-family: var(--font-heading), sans-serif;
    font-size: 2rem;
  }

  div.thumbs-container {
    background: rgba(var(--thumbs-grid-container-bg-color-rgb), 0.8);
    color: white;
    position: fixed;
    width: 100%;
    max-width: 100%;
    height: 100%;
    z-index: 1000;
    left: 0;
    bottom: -101%;
    display: block;
    transition: all 1000ms;
    visibility: hidden;
  }

  .thumbs-container.active {
    bottom: 0;
    visibility: visible;
  }

  .thumbs-container main {
    width: 100%;
    height: 100%;
    height: calc(100% - 165px);
    position: relative;
  }

  .thumbsgrid {
    height: 100%;
    overflow-y: scroll;
    overflow-x: hidden;
  }

  ::-webkit-scrollbar {
    width: 10px;
  }
  ::-webkit-scrollbar-thumb {
    background: #fff;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: #ddd;
  }

  .bottom-menu {
    background: rgba(var(--thumbs-grid-bottom-menu-bg-color-rgb), 0.8);
    position: fixed;
    left: 50%;
    bottom: 0;
    transform: translateX(-50%);
    z-index: 1001;
    padding: 0.5rem;
    border-radius: 10px 10px 0 0;
    min-width: 185px;
  }

  .bottom-menu:hover {
    background: rgba(var(--thumbs-grid-bottom-menu-bg-color-rgb), 1);
    border: 3px solid var(--thumbs-grid-bottom-menu-text-color);
    border-width: 3px 3px 0 3px;
  }

  button {
    appearance: none;
    margin: 0;
    padding: 0;
    background: none;
    color: inherit;
    font: inherit;
    border: none;
    cursor: pointer;
    display: block;
    text-align: left;
    width: 100%;
    height: auto;
  }

  .thumbs-button {
    /* reset */
    appearance: none;
    margin: 0;
    padding: 0;
    background: none;
    color: inherit;
    font: inherit;
    border: none;
    cursor: pointer;
    display: block;
    text-align: left;
    width: 100%;
    height: auto;
    /* css */
    color: var(--thumbs-grid-bottom-menu-text-color);
    font-family: var(--font-heading), sans-serif;
    font-weight: 300;
    font-size: 1.5rem;
    white-space: nowrap;
    display: flex;
    justify-content: center;
    align-items: center;
    min-width: 45px;
    min-height: 45px;
    margin: 0 8px;
  }

  .icon {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 35px;
    height: 35px;
  }

  .icon svg {
    width: 100%;
    height: 100%;
  }

  .next-button .icon svg {
    transform: scaleX(-1);
  }

  .thumbs-button .icon {
    margin-right: 10px;
  }

  @media (max-width: 600px) {
    .thumbs-button .icon {
      margin-right: 0;
    }
  }

  .thumb {
    /* reset */
    appearance: none;
    margin: 0;
    padding: 0;
    background: none;
    color: inherit;
    font: inherit;
    border: none;
    cursor: pointer;
    display: block;
    text-align: left;
    width: 100%;
    height: 100%;
    /* css */
    border: 3px solid transparent;
    border-radius: 10px;
    cursor: pointer;
    color: var(--thumbs-grid-text-color);
  }

  .thumb:hover,
  .thumb:focus {
    border: 3px solid #fff;
    outline: none;
  }

  .thumb:focus {
    border: 3px solid white !important;
  }

  .thumb:focus-visible {
    outline: 4px solid var(--focus-color);
    outline-offset: 0;
    border-radius:  10px !important;
  }

  .thumbsgrid-view li.active .thumb {
    border: 3px solid #fff;
  }

  .thumbsgrid-view {
    margin: 3rem 0;
    list-style: none;
    margin: 0;
    padding: 0;
    border: none;

    display: grid;
    grid-gap: 0.5rem;
    grid-template-columns: repeat(auto-fill, minmax(275px, 1fr));
    margin: auto;
    width: 95%;
    margin-bottom: 100px;
  }

  .thumbsgrid-view figure {
    width: 100%;
    height: 100%;
  }

  .thumbsgrid-view img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 10px;
  }

  .thumbsgrid-view figure {
    position: relative;
    padding: 0;
    margin: 0;
  }

  .thumbsgrid-view figcaption {
    position: absolute;
    bottom: 4px;
    left: 50%;
    transform: translateX(-50%);
    width: 90%;
    text-align: center;
  }

  .thumbsgrid-view figcaption p {
    background: rgba(var(--thumbs-grid-text-bg-color-rgb), 0.8);

    padding: 8px;
    margin: 0;
    display: inline-block;
    border-radius: 5px;
    text-align: center;
    font-size: 1em;
    font-weight: 500;
    font-family: var(--font-body), sans-serif;
  }

  .scene-categories {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
  }

  .scene-categories h2 {
    font-family: var(--font-heading), sans-serif;
    font-size: 1.5rem;
    margin: 0;
  }

  .scene-categories ol {
    list-style: none;
    margin: 0.25rem 0;
    border: none;
    padding: 0.5em;
    border-radius: 6px;
  }

  .scene-categories li {
    border: 2px solid #fff;
    display: inline-block;
    padding: 0.25rem 1.25rem;
    margin: 0.15rem;
    cursor: pointer;
    border-radius: 6px;
    font-size: 1.3rem;
    font-family: var(--font-heading), sans-serif;
    background: #fff;
    color: #000;
  }

  .scene-categories li:hover {
    background: #fff;
    color: var(--theme-color);
    border-color: var(--theme-color);
  }

  .scene-categories li.active {
    background: #fff;
    color: #000;
    background: rgba(var(--theme-color-rgb), 0.8);
    color: #fff;
    border-color: rgba(var(--theme-color-rgb), 0.8);
  }

  .scene-categories label {
    font-family: var(--font-heading), sans-serif;
    font-size: 1.5rem;
  }

  .scene-categories select {
    padding: 10px;
    margin: 10px 0;
    border-radius: 5px;
    border: 2px solid #ffffff;
    font-size: 22px;
    min-width: 25%;
    max-width: 95%;
    background: rgba(var(--theme-color-rgb), 0.8);
    color: #ffffff;
    font-family: var(--font-heading), sans-serif;
  }

  main.has-category-sidebar {
    display: flex;
  }

  main.has-category-sidebar .thumbsgrid {
    flex: 1;
  }

  .category-sidebar {
    min-width: 200px;
    overflow-y: scroll;
    padding: 10px;
    margin: 0 0 0 18px;
    font-family: var(--font-heading), sans-serif;
  }

  .category-sidebar a {
    font-family: var(--font-body), sans-serif;
    display: block;
    color: #fff;
    padding: 10px;
    text-decoration: none;
    font-size: 1.15rem;
  }

  .category-sidebar a:hover {
    background-color: #555;
    text-decoration: underline;
    border-radius: 8px;
  }

  .category-sidebar ol {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .category-sidebar li {
  }
  .category-sidebar li:hover {
  }
  .category-sidebar li.active {
  }

  .category-sidebar li.active .category {
    /* background-color: rgba(var(--theme-color-rgb), 0.8); */
    background-color: #000;
    text-decoration: underline;
    border-radius: 8px;
    font-weight: 600;
  }

  .category {
    /* reset */
    appearance: none;
    font-size: inherit;
    color: inherit;
    background: none;
    border: none;
    padding: 0;
    margin: 0;
    width: 100%;
    height: 100%;
    text-align: inherit;
    text-decoration: inherit;
    cursor: pointer;
    /* css */
    margin: 6px 0;
    padding: 10px;
    font-family: var(--font-body), sans-serif;
    cursor: pointer;
    font-size: 1.15rem;
    color: #fff;
    border: 2px solid transparent;
  }

  .category:hover {
    text-decoration: underline;
    border-radius: 8px;
    border: 2px solid #fff;
  }

  /* Next / Prev */
  .prev-button,
  .next-button,
  .thumbs-button {
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .next-button img {
    transform: scaleX(-1);
  }

  .bottom-menu {
    display: flex;
  }

  @media only screen and (max-width: 600px) {
    .thumbsgrid-view {
      grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    }

    .thumbs-container main {
      height: calc(100% - 176px);
    }

    h1.tour-title {
      font-size: 2rem;
    }

    .thumbsgrid-view figcaption p {
      font-size: 12px;
    }

    .thumbs-button {
      font-size: 1.25rem;
    }

    .bottom-menu {
      width: auto;
    }

    .bottom-menu:hover {
      border: none;
    }
  }
</style>
